import React from 'react';
import { Helmet } from 'react-helmet-async';
import {
  FaWandMagicSparkles,
  FaGift,
  FaDice,
  FaBolt,
  FaCircleCheck,
} from 'react-icons/fa6';
import { CASINOS } from '../data/casinos';
import CasinoCard from '../components/casinocard';
import RgCallout from '../components/rgcallout';
import './innerpage.css';

const WHAT_NEW_MEANS = [
  { Icon: FaWandMagicSparkles, title: 'Fresh Branding', text: 'New sites often launch with modern, mobile-first interfaces and faster-loading game lobbies.' },
  { Icon: FaGift, title: 'Launch Bonuses', text: 'Many new casinos offer generous introductory bonuses to attract their first wave of players.' },
  { Icon: FaDice, title: 'Latest Games', text: 'New sites frequently integrate the newest slot releases before established operators add them.' },
  { Icon: FaBolt, title: 'Faster Tech', text: 'Modern platforms built on newer infrastructure tend to offer faster load times and smoother withdrawals.' },
];

export default function NewCasinoSites() {
  return (
    <>
      <Helmet>
        <title>New UK Casino Sites 2026 | Latest UKGC-Licensed Launches | London Casino Deals</title>
        <meta name="description" content="Discover the newest UKGC-licensed casino sites in the UK. Our experts review fresh casino launches, checking licences, bonuses, games, and safety tools before recommending them." />
        <meta property="og:title" content="New Casino Sites UK 2026 — Latest Launches Reviewed | London Casino Deals" />
        <meta property="og:description" content="Reviewed and verified new UK casino sites for 2026. Only UKGC-licensed, only sites that pass our full safety and quality checklist." />
        <link rel="canonical" href="https://londoncasinodeals.com/new-casino-sites" />
      </Helmet>

      <div className="inner-hero">
        <div className="container">
          <div className="inner-hero__breadcrumb">
            <a href="/">Home</a> <span aria-hidden="true">›</span> New Casino Sites
          </div>
          <h1 className="inner-hero__title">New UK Casino Sites 2026</h1>
          <p className="inner-hero__subtitle">
            Reviewed and verified · UKGC-licensed only · Updated regularly · 18+ T&amp;Cs apply
          </p>
        </div>
      </div>

      <div className="inner-page-rg-slot">
        <div className="container">
          <RgCallout variant="compact" />
        </div>
      </div>

      <section className="section section--white">
        <div className="container">
          <div className="inner-intro">
            <h2>What to Look for in a New Casino Site</h2>
            <p>
              Hundreds of new online casinos launch each year. While a fresh interface or exciting welcome bonus can be attractive, it's essential to verify that any new site meets the same standards as established operators. At London Casino Deals, we apply the same 12-point review checklist to new launches as we do to long-running sites.
            </p>
            <p>
              The most important check is always the UKGC licence. You can verify any operator's licence status directly on the{' '}
              <a href="https://www.gamblingcommission.gov.uk/" target="_blank" rel="noopener noreferrer nofollow">
                UK Gambling Commission's public register
              </a>.
            </p>
          </div>

          <div className="new-features-grid">
            {WHAT_NEW_MEANS.map((item) => (
              <div key={item.title} className="new-feature-card">
                <div className="new-feature-card__icon" aria-hidden="true">
                  <item.Icon size={28} />
                </div>
                <h3 className="new-feature-card__title">{item.title}</h3>
                <p className="new-feature-card__text">{item.text}</p>
              </div>
            ))}
          </div>
          <RgCallout variant="boxed" className="inner-intro-rg" />
        </div>
      </section>

      <section className="section section--darker">
        <div className="container">
          <div className="section__header">
            <span className="section__eyebrow">Our Picks</span>
            <h2 className="section__title">Reviewed Casino Sites — All Verified for 2026</h2>
            <p className="section__subtitle">Every site below has been reviewed by our editorial team and holds a valid UKGC licence</p>
          </div>
          <div className="casino-grid-3">
            {CASINOS.map((casino, i) => (
              <CasinoCard key={casino.id} casino={casino} rank={i + 1} />
            ))}
          </div>
          <RgCallout variant="compact" className="inner-page-rg-between" />
        </div>
      </section>

      <section className="section section--white">
        <div className="container">
          <div className="inner-checklist">
            <h2>Our New Casino Site Checklist</h2>
            <p>Before we recommend any casino site — new or established — it must pass every item on this checklist:</p>
            <div className="checklist-grid">
              {[
                'Active UKGC licence, verified on the public register',
                'SSL encryption and secure payment processing',
                'Identity verification (KYC) process in place',
                'Responsible gambling tools: deposit limits, session limits, self-exclusion',
                'GAMSTOP integration for UK self-exclusion',
                'Clear, accessible terms and conditions',
                'Transparent bonus terms with reasonable wagering requirements',
                'Multiple tested withdrawal methods with stated processing times',
                'Mobile-optimised experience (tested on iOS and Android)',
                'Reachable and responsive customer support team',
                'Game library from verified, certified software providers',
                'Fair complaints and dispute resolution process',
              ].map((item) => (
                <div key={item} className="checklist-item">
                  <span className="checklist-item__icon" aria-hidden="true">
                    <FaCircleCheck size={18} />
                  </span>
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>
          <p className="inner-disclaimer">
            18+ only · UK residents only · All bonuses subject to T&amp;Cs · Wagering requirements apply · Gamble responsibly · <a href="https://www.begambleaware.org/" target="_blank" rel="noopener noreferrer nofollow">BeGambleAware.org</a>
          </p>
        </div>
      </section>
    </>
  );
}
